import {useAppContext} from "../../context/AppContext";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Select, Empty, Button, Space} from "antd";
import {UpOutlined, DownOutlined, FullscreenOutlined, FullscreenExitOutlined} from "@ant-design/icons";
import StationElevationPlot2 from "../outputs/ui/StationElevationPlot2";
import {StaElevPlotLayer} from "../outputs/ui/StationElevationPlot";
import {GetMessageCodeText} from "../outputs/MessageCodes";
import {readPlot2, writePlot2} from "./plot2Store";
import ResultTable from "../../components/table/ResultTable";

// One independent cell of the Plot-2 grid. Each cell carries its OWN message-code +
// cross-section selectors (for side-by-side comparison), navigation, a layer/profile
// picker, vertical-exaggeration control, a value readout, and a forked
// StationElevationPlot2. Plot data is read from getXsPlots() (all XS of every parsed
// river/reach), independent of map_base and of the check-message result rows.
// Selections persist across tab switches via plot2Store (keyed by persistKey).
const N = StaElevPlotLayer.StaElevLayerNames
const S = StaElevPlotLayer.StructureLayouts

// Type-based default enabled layers — used when the selected XS's type (xs vs
// structure) does not match the selected message's Plot.Type, or when there is no
// message plot config. Mirrors the common patterns in Operations.js.
const XS_DEFAULT_ON = [N.B_STA_ELEV, N.B_STREAM_CENTER, N.B_THALWEG, N.B_BANKS, N.B_WSEL_YR100]
const STRUCT_DEFAULT_ON = [N.B_STA_ELEV, N.B_STREAM_CENTER, N.B_BANKS, S.B_LOW_CHORDS, S.B_HIGH_CHORDS, N.B_XS_INEFF, N.B_WSEL_YR100, N.B_WEIR_STA_ELEV]

const withCmp = (base, hasCmp) => hasCmp ? [...base, ...base.map(l => l.replace('B:', 'C:'))] : base

// river-aware so ids stay unique across rivers (same RS number can exist in two rivers).
const idOf = (x) => `${x.river}___${x.reach}__${x.type}:${x.cross_section_value}`
const matchesRs = (m, x) => String(m) === String(x.cross_section) || String(m).includes(String(x.cross_section_value))

const fmt = (v) => (v === null || v === undefined || isNaN(v)) ? '' : Number(v).toFixed(2)

// Available legend layers for an entry (union across its plot payloads).
const availableLayers = (entry) => {
    if (!entry) return []
    let a
    if (entry.type === 'structure') {
        a = [
            ...StaElevPlotLayer.getLayersFromData('B', entry.plot_us_base),
            ...StaElevPlotLayer.getLayersFromData('B', entry.plot_ds_base),
            ...StaElevPlotLayer.getLayersFromData('C', entry.plot_us_cmp),
            ...StaElevPlotLayer.getLayersFromData('C', entry.plot_ds_cmp),
        ]
    } else {
        a = [
            ...StaElevPlotLayer.getLayersFromData('B', entry.plot_base),
            ...StaElevPlotLayer.getLayersFromData('C', entry.plot_cmp),
        ]
    }
    return Array.from(new Set(a))
}

export default function Plot2Cell({cellIndex, persistKey, width, height, sync, sharedLayers, onLayersChange, canExpand, expanded, onToggleExpand}) {
    const {
        getXsPlots, getOperationItemConfig, getMessageCodeList,
        selectedOperation, selectedErrorId
    } = useAppContext()

    const xs = getXsPlots() || {items: []}
    const items = xs.items || []
    const messageCodes = Array.from(new Set(getMessageCodeList() || []))

    // Restore persisted selections (survives tab switches). See plot2Store.
    const saved = readPlot2(persistKey)
    const [cellErrorId, setCellErrorId] = useState(saved.cellErrorId ?? (selectedErrorId || messageCodes[0]))
    const [selectedId, setSelectedId] = useState(saved.selectedId ?? null)
    const [localLayers, setLocalLayers] = useState(saved.localLayers ?? null) // null => computed default
    const [valuesOpen, setValuesOpen] = useState(true)

    useEffect(() => {
        writePlot2(persistKey, {cellErrorId, selectedId, localLayers})
    }, [persistKey, cellErrorId, selectedId, localLayers]);

    const showItems = items

    // This cell's message (per-panel) -> its flagged RS list (grouped on top).
    const rsListForError = (errorId) =>
        selectedOperation?.data?.result?.find(x => GetMessageCodeText(selectedOperation?.key, x.error_id) === errorId)?.data?.map(r => r.cross_section) || []
    const cellMsgRs = rsListForError(cellErrorId)
    const isMessageXs = (x) => cellMsgRs.some(m => matchesRs(m, x))
    const msgEntries = showItems.filter(isMessageXs)

    const firstEntryForError = (errorId) => {
        const rs = rsListForError(errorId)
        return showItems.find(x => rs.some(m => matchesRs(m, x)))
    }

    // Reset this cell's message when the OPERATION changes (sidebar) — but skip the
    // initial mount so a restored cellErrorId isn't clobbered on a tab switch.
    const opKeyRef = useRef(selectedOperation?.key)
    useEffect(() => {
        if (opKeyRef.current === selectedOperation?.key) return
        opKeyRef.current = selectedOperation?.key
        setCellErrorId(selectedErrorId || messageCodes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOperation?.key]);

    // Default XS selection: keep a valid restored selection, else this cell's first
    // message XS, else the first XS.
    useEffect(() => {
        if (selectedId && showItems.some(x => idOf(x) === selectedId)) return
        const first = msgEntries[0] || showItems[0]
        setSelectedId(first ? idOf(first) : null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showItems.length]);

    const entry = showItems.find(x => idOf(x) === selectedId)
    const isStructure = entry?.type === 'structure'
    const primaryPlot = entry ? (isStructure ? entry.plot_us_base : entry.plot_base) : null
    const hasCmp = entry ? (isStructure ? !!(entry.plot_us_cmp || entry.plot_ds_cmp) : !!entry.plot_cmp) : false

    // Computed default layers: the operation's onChangeRS if the XS type matches its
    // Plot.Type, otherwise a type-based fallback.
    const defaultLayers = useMemo(() => {
        if (!entry) return []
        const plotCfg = getOperationItemConfig()?.Plot
        const cfgType = plotCfg?.Type
        const typeMatches = plotCfg && (
            (isStructure && cfgType === 'Structure') ||
            (!isStructure && cfgType === 'CrossSection')
        )
        if (typeMatches && plotCfg.onChangeRS) {
            try {
                const res = plotCfg.onChangeRS(entry)
                if (res && res.length) return res
            } catch (e) { /* fall through to type default */ }
        }
        return withCmp(isStructure ? STRUCT_DEFAULT_ON : XS_DEFAULT_ON, hasCmp)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, selectedOperation?.key]);

    // Fresh per-XS default when the XS changes — but skip the initial mount so a
    // restored localLayers isn't clobbered on a tab switch.
    const selIdRef = useRef(selectedId)
    useEffect(() => {
        if (selIdRef.current === selectedId) return
        selIdRef.current = selectedId
        setLocalLayers(null)
    }, [selectedId]);

    // Seed shared layers from the first cell when "Sync layers" turns on.
    useEffect(() => {
        if (sync && sharedLayers == null && cellIndex === 0 && onLayersChange && entry) {
            onLayersChange(localLayers || defaultLayers)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sync]);

    // Effective layers: shared (while "Sync layers" is on) > local override > default.
    const effectiveLayers = (sync && sharedLayers) ? sharedLayers : (localLayers || defaultLayers)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options = useMemo(() => availableLayers(entry), [selectedId])

    const onLayersSelect = (v) => {
        setLocalLayers(v)
        if (sync && onLayersChange) onLayersChange(v)
    }

    // --- Navigation: per-dropdown up/down steppers --------------------------
    const stepIn = (list, delta) => {
        const i = list.findIndex(x => idOf(x) === selectedId)
        const n = (i < 0 && delta > 0) ? list[0] : list[i + delta]
        if (n) setSelectedId(idOf(n))
    }
    const msgIdx = msgEntries.findIndex(x => idOf(x) === selectedId)
    const allIdx = showItems.findIndex(x => idOf(x) === selectedId)

    // --- Profiles (WSEL) — a focused view over the WSEL layers --------------
    const availableProfiles = useMemo(() => {
        if (!entry) return []
        const arr = []
        const collect = (p) => (p?.wsel || []).forEach(w => { if (w?.profile) arr.push(w.profile) })
        if (isStructure) { collect(entry.plot_us_base); collect(entry.plot_ds_base) } else collect(entry.plot_base)
        return Array.from(new Set(arr))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId])
    const selectedProfiles = availableProfiles.filter(p => (effectiveLayers || []).includes(`B: WSEL ${p}`))
    const onProfilesChange = (profiles) => {
        const nonWsel = (effectiveLayers || []).filter(l => !String(l).includes('WSEL'))
        const wsel = profiles.flatMap(p => hasCmp ? [`B: WSEL ${p}`, `C: WSEL ${p}`] : [`B: WSEL ${p}`])
        onLayersSelect([...nonWsel, ...wsel])
    }

    // --- Value readout (display only — no derivation in the UI) -------------
    const valueRows = useMemo(() => {
        const p = primaryPlot
        const rows = []
        if (p?.banks?.x?.length) {
            if (p.banks.x[0] !== null && p.banks.x[0] !== undefined) rows.push({label: 'Left bank', sta: p.banks.x[0], elev: p.banks.y?.[0]})
            if (p.banks.x[1] !== null && p.banks.x[1] !== undefined) rows.push({label: 'Right bank', sta: p.banks.x[1], elev: p.banks.y?.[1]})
        }
        if (p?.thalweg?.x?.length) rows.push({label: 'Min channel', sta: p.thalweg.x[0], elev: p.thalweg.y?.[0]})
        return rows
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId])
    const wselRows = useMemo(() => (primaryPlot?.wsel || []).filter(w => w?.y?.length).map(w => ({profile: w.profile, elev: w.y[0]})),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedId])

    const globalVariables = selectedOperation?.data?.global_variables
    const plotHeight = isStructure ? Math.floor((height || 500) / 2) : height


    const renderOption = (x) => (
        <Select.Option key={idOf(x)} value={idOf(x)}>
            {x.cross_section}{x.type === 'structure' ? ' · Str' : ''}
        </Select.Option>
    )

    const btn = (icon, title, onClick, disabled) => (
        <Button size={'small'} icon={icon} title={title} onClick={onClick} disabled={disabled}/>
    )

    return (
        <div style={{border: '1px solid #f0f0f0', borderRadius: 4, padding: 6, minWidth: 0}}>
            <div style={{display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap', alignItems: 'center'}}>
                <Select
                    size={'small'} style={{width: 110}} value={cellErrorId}
                    onChange={(v) => {
                        setCellErrorId(v)
                        const fe = firstEntryForError(v)
                        if (fe) setSelectedId(idOf(fe))
                    }}
                    suffixIcon={<b style={{fontSize: 11}}>Msg</b>}
                    placeholder={'Message'}
                    options={messageCodes.map(c => ({value: c, label: c}))}
                />
                <Space size={0}>
                    <Select
                        size={'small'} style={{width: 140}} showSearch
                        value={msgEntries.some(x => idOf(x) === selectedId) ? selectedId : undefined}
                        onChange={setSelectedId}
                        disabled={msgEntries.length === 0}
                        placeholder={msgEntries.length ? `${cellErrorId || 'Msg'} RS` : 'No message RS'}
                        optionFilterProp={'children'}
                    >
                        {msgEntries.map(renderOption)}
                    </Select>
                    <Button size={'small'} icon={<UpOutlined/>} onClick={() => stepIn(msgEntries, -1)} disabled={msgIdx <= 0}/>
                    <Button size={'small'} icon={<DownOutlined/>} onClick={() => stepIn(msgEntries, 1)} disabled={msgEntries.length === 0 || msgIdx >= msgEntries.length - 1}/>
                </Space>
                <span style={{fontSize: 11, color: '#888', flexShrink: 0}}>All RS:</span>
                <Space size={0}>
                    <Select
                        size={'small'} style={{width: 200}} showSearch value={selectedId}
                        onChange={setSelectedId} placeholder={'Cross section'} optionFilterProp={'children'}
                    >
                        {showItems.map(renderOption)}
                    </Select>
                    <Button size={'small'} icon={<UpOutlined/>} onClick={() => stepIn(showItems, -1)} disabled={allIdx <= 0}/>
                    <Button size={'small'} icon={<DownOutlined/>} onClick={() => stepIn(showItems, 1)} disabled={showItems.length === 0 || allIdx >= showItems.length - 1}/>
                </Space>
                {canExpand && btn(expanded ? <FullscreenExitOutlined/> : <FullscreenOutlined/>, expanded ? 'Back to grid' : 'Expand this panel', () => onToggleExpand && onToggleExpand())}
                {entry && (
                    <Button size={'small'} type={valuesOpen ? 'primary' : 'default'} onClick={() => setValuesOpen(o => !o)}>Values</Button>
                )}
                {entry && (
                    <Select
                        size={'small'} mode={'multiple'} maxTagCount={2} style={{flex: 1, minWidth: 150, maxWidth: 300}}
                        placeholder={'Layers'} value={effectiveLayers} onChange={onLayersSelect}
                        options={options.map(l => ({value: l, label: l.replace('B: ', '').replace('C: ', '')}))}
                    />
                )}
                {entry && (
                    <Select
                        size={'small'} mode={'multiple'} maxTagCount={2} style={{flex: 1, minWidth: 150, maxWidth: 300}}
                        disabled={availableProfiles.length === 0}
                        placeholder={availableProfiles.length ? 'WSEL profiles' : 'No profiles'}
                        value={selectedProfiles} onChange={onProfilesChange}
                        options={availableProfiles.map(p => ({value: p, label: p}))}
                    />
                )}
            </div>


            {!entry && (
                <Empty
                    description={items.length === 0
                        ? 'Bu run Plot-2 verisi icermiyor (yeniden calistirin)'
                        : (showItems.length === 0 ? 'Bu river icin veri yok' : 'Cross section secin')}
                    style={{padding: 24}}
                />
            )}
            {entry && !isStructure && (
                <StationElevationPlot2
                    layers={effectiveLayers} globalVariables={globalVariables}
                    crossSection={`${entry.cross_section}`}
                    plotData={{base: entry.plot_base, cmp: entry.plot_cmp}}
                    width={width} height={plotHeight}
                />
            )}
            {entry && isStructure && (
                <>
                    <StationElevationPlot2
                        layers={effectiveLayers} globalVariables={globalVariables}
                        crossSection={`${entry.cross_section} US`}
                        plotData={{base: entry.plot_us_base, cmp: entry.plot_us_cmp}}
                        width={width} height={plotHeight}
                    />
                    <StationElevationPlot2
                        layers={effectiveLayers} globalVariables={globalVariables}
                        crossSection={`${entry.cross_section} DS`}
                        plotData={{base: entry.plot_ds_base, cmp: entry.plot_ds_cmp}}
                        width={width} height={plotHeight}
                    />
                </>
            )}

            {entry && valuesOpen && (
                <div style={{marginTop: 4, fontSize: 11}}>
                    <div style={{fontWeight: 600, color: '#555', marginBottom: 2}}>Values</div>
                    <ResultTable maxWidth={300} noDescription>
                        <thead>
                            <tr style={{color: '#888'}}>
                                <th style={{textAlign: 'left', fontWeight: 400, paddingRight: 16}}>Item</th>
                                <th style={{textAlign: 'right', fontWeight: 400, paddingLeft: 16}}>Station</th>
                                <th style={{textAlign: 'right', fontWeight: 400, paddingLeft: 16}}>Elev</th>
                            </tr>
                        </thead>
                        <tbody>
                            {valueRows.map((r, i) => (
                                <tr key={`v${i}`} style={{borderTop: '1px solid #f5f5f5'}}>
                                    <td style={{paddingRight: 16}}>{r.label}</td>
                                    <td style={{textAlign: 'right', paddingLeft: 16}}>{fmt(r.sta)}</td>
                                    <td style={{textAlign: 'right', paddingLeft: 16}}>{fmt(r.elev)}</td>
                                </tr>
                            ))}
                            {wselRows.map((w, i) => (
                                <tr key={`w${i}`} style={{borderTop: '1px solid #f5f5f5'}}>
                                    <td style={{paddingRight: 16}}>WSEL {w.profile}</td>
                                    <td/>
                                    <td style={{textAlign: 'right', paddingLeft: 16}}>{fmt(w.elev)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </ResultTable>
                </div>
            )}
        </div>
    )
}
